import React, { useState, useEffect } from 'react';
import type { BankAccount, AccountType } from '../types/account';
import { createAccount, updateAccount } from '../services/accountService';
import { X, DollarSign, Building2, CreditCard, Hash, Type } from 'lucide-react';
import { APP_CURRENCY } from '../config/currency';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingAccount?: BankAccount | null;
}

const CATEGORIES: { value: AccountType; label: string }[] = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'investment', label: 'Investment' },
  { value: 'cash', label: 'Cash / Wallet' },
  { value: 'other', label: 'Other' },
];

const CURRENCIES = [APP_CURRENCY];

const COLORS = [
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#10b981', // emerald
  '#f59e0b', // amber
  '#f43f5e', // rose
  '#06b6d4', // cyan
];

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose, onSaved, editingAccount }) => {
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('checking');
  const [balance, setBalance] = useState('');
  const [currency, setCurrency] = useState(APP_CURRENCY);
  const [accountNumber, setAccountNumber] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingAccount) {
      setBankName(editingAccount.bankName);
      setAccountName(editingAccount.accountName);
      setAccountType(editingAccount.accountType);
      setBalance(String(editingAccount.balance));
      setCurrency(editingAccount.currency);
      setAccountNumber(editingAccount.accountNumberLast4 || '');
      setColor(editingAccount.color || COLORS[0]);
    } else {
      setBankName(''); setAccountName(''); setAccountType('checking');
      setBalance(''); setCurrency(APP_CURRENCY); setAccountNumber(''); setColor(COLORS[0]);
    }
  }, [editingAccount, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bankName.trim() || !accountName.trim() || !balance || isNaN(Number(balance))) return;
    setLoading(true);
    const data = {
      bankName: bankName.trim(),
      accountName: accountName.trim(),
      accountType,
      balance: Number(balance),
      currency,
      accountNumberLast4: accountNumber.trim() || undefined,
      color,
    };
    if (editingAccount) {
      await updateAccount(editingAccount.id, data);
    } else {
      await createAccount(data);
    }
    setLoading(false);
    onSaved();
    onClose();
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 bg-white/5 border border-white/10 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl fade-in-up" style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h2 className="text-lg font-semibold text-white">{editingAccount ? 'Edit Account' : 'Add Bank Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Bank & Acc Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Bank Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className={`${inputClass} pl-9`} placeholder="e.g. Chase" value={bankName} onChange={(e) => setBankName(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className={labelClass}>Account Name</label>
              <div className="relative">
                <Type className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className={`${inputClass} pl-9`} placeholder="e.g. Savings" value={accountName} onChange={(e) => setAccountName(e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Type + Number */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Type</label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <select className={`${inputClass} pl-9 appearance-none`} value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType)}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value} className="bg-gray-800">{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>ACCT ending in <span className="text-gray-600">(opt)</span></label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className={`${inputClass} pl-9 tracking-widest`} placeholder="1234" maxLength={4}
                  value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))} />
              </div>
            </div>
          </div>

          {/* Amount + Currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelClass}>Current Balance</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className={`${inputClass} pl-9 text-cyan-400 font-bold`} type="number" step="0.01" placeholder="0.00"
                  value={balance} onChange={(e) => setBalance(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select className={`${inputClass} appearance-none`} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c} className="bg-gray-800">{c}</option>)}
              </select>
            </div>
          </div>

          {/* Color theme */}
          <div>
            <label className={labelClass}>Card Theme Color</label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button
                  key={c} type="button" onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-all border-2 ${color === c ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  style={{ backgroundColor: c }}
                  title="Card Color"
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/25">
              {loading ? 'Saving…' : editingAccount ? 'Update Account' : 'Add Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountModal;
